import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  signal,
} from '@angular/core';
import { ButtonComponent } from '@fundamental-ngx/core/button';
import { ContentDensityDirective } from '@fundamental-ngx/core/content-density';
import {
  IllustratedMessageActionsComponent,
  IllustratedMessageComponent,
  IllustratedMessageFigcaptionComponent,
  IllustratedMessageTextDirective,
  IllustratedMessageTitleDirective,
} from '@fundamental-ngx/core/illustrated-message';
import {
  addInitListener,
  linkManager,
} from '@luigi-project/client/luigi-client';
import { I18nService, LuigiCoreService } from '@openmfp/portal-ui-lib';
import {
  ButtonConfig,
  DxpErrorComponentConfig,
  SceneConfig,
} from 'src/app/models/dxp-error';

@Component({
  selector: 'app-error',
  imports: [
    IllustratedMessageComponent,
    IllustratedMessageActionsComponent,
    IllustratedMessageFigcaptionComponent,
    IllustratedMessageTitleDirective,
    IllustratedMessageTextDirective,
    ButtonComponent,
    ContentDensityDirective,
  ],
  templateUrl: './error.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.ShadowDom,
})
export class ErrorComponent implements AfterViewInit {
  readonly config = signal<DxpErrorComponentConfig>({
    sceneConfig: {
      scene: {
        url: '',
        id: '',
      },
    },
    illustratedMessageTitle: '',
    illustratedMessageText: '',
    buttons: [],
  });

  readonly sceneConfig = signal<SceneConfig>({
    scene: {
      url: '',
      id: '',
    },
  });

  errorCode!: string;
  entityNotFoundError: any;

  constructor(
    private luigiCoreService: LuigiCoreService,
    private i18nService: I18nService,
  ) {}

  async ngAfterViewInit() {
    this.luigiCoreService.ux().hideAppLoadingIndicator();
    this.i18nService.afterInit();

    this.errorCode = globalThis.location.hash;
    if (this.errorCode) {
      this.errorCode = this.errorCode.replace('#', '');
      if (this.errorCode.includes('/')) {
        this.errorCode = this.errorCode.split('/')[0];
      }
    }
    if (this.errorCode.startsWith('entity_')) {
      addInitListener(async (ctx) => {
        if (ctx.error) {
          this.entityNotFoundError = ctx.error;
        }
        await this.setSceneConfig();
      });
    } else {
      await this.setSceneConfig();
    }
  }

  goTo(button: ButtonConfig): void {
    if (button.url) {
      window.open(button.url, '_blank');
    } else if (button.route) {
      linkManager().fromContext(button.route.context).navigate('/');
    }
  }

  private async setSceneConfig() {
    if (this.entityNotFoundError) {
      const type = this.entityNotFoundError.entityDefinition?.dynamicFetchId;
      const typeStr = this.entityNotFoundError.entityDefinition?.label;
      const typeStrPlural: string =
        this.entityNotFoundError.entityDefinition?.pluralLabel;

      const sceneId =
        this.entityNotFoundError.entityDefinition?.notFoundConfig
          ?.sapIllusSVG || 'Scene-NoSearchResults';

      const id = this.entityNotFoundError.additionalContext[type];
      const gotoNavContext =
        this.entityNotFoundError.entityDefinition?.notFoundConfig
          ?.entityListNavigationContext;
      const buttons = [];

      if (gotoNavContext && typeStrPlural) {
        buttons.push({
          route: {
            context: gotoNavContext,
          },
          label: await this.i18nService.getTranslationAsync(
            'ERROR_ENTITY_VIEW_ALL_BUTTON',
            { entityTypePlural: typeStrPlural },
          ),
        });
      }

      if (this.errorCode.includes('404')) {
        this.config.set(
          await this.getErrorEntityNotFoundConfig(
            id,
            sceneId,
            typeStr,
            typeStrPlural,
            gotoNavContext,
            buttons,
          ),
        );
      } else {
        this.config.set(await this.getErrorDefaultConfig());
      }
    } else {
      switch (this.errorCode) {
        case '403': {
          this.config.set(await this.getError403Config());
          break;
        }
        case '404': {
          this.config.set(await this.getError404Config());
          break;
        }
        default: {
          this.config.set(await this.getErrorDefaultConfig());
        }
      }
    }
    this.sceneConfig.set(this.config().sceneConfig);
  }

  private async getError404Config() {
    return {
      sceneConfig: {
        scene: {
          url: 'assets/moments/sapIllus-Scene-NoEntries.svg',
          id: 'sapIllus-Scene-NoEntries',
        },
      },
      illustratedMessageTitle: await this.i18nService.getTranslationAsync(
        'ERROR_CONTENT_NOT_FOUND_TITLE',
      ),
      illustratedMessageText: await this.i18nService.getTranslationAsync(
        'ERROR_CONTENT_NOT_FOUND_TEXT',
      ),
      buttons: [
        {
          url: 'https://jira.tools.sap/secure/CreateIssue.jspa?pid=109355&issuetype=1',
          label: await this.i18nService.getTranslationAsync(
            'ERROR_CONTENT_NOT_FOUND_CREATE_ISSUE_BUTTON',
          ),
        },
      ],
    };
  }

  private async getError403Config() {
    return {
      sceneConfig: {
        scene: {
          url: 'assets/moments/tnt-Scene-UnsuccessfulAuth.svg',
          id: 'tnt-Scene-UnsuccessfulAuth',
        },
      },
      illustratedMessageTitle: '',
      illustratedMessageText: await this.i18nService.getTranslationAsync(
        'ERROR_CONTENT_NOT_ALLOWED_NO_PROJECT_MEMBER_TEXT',
      ),
      buttons: [
        {
          url: '',
          label: await this.i18nService.getTranslationAsync(
            'ERROR_CONTENT_NOT_ALLOWED_JOIN_PROJECT_BUTTON',
          ),
        },
        {
          url: '',
          label: await this.i18nService.getTranslationAsync(
            'ERROR_CONTENT_NOT_ALLOWED_VIEW_PROJECT_BUTTON',
          ),
        },
      ],
    };
  }

  private async getErrorDefaultConfig() {
    return {
      sceneConfig: {
        scene: {
          url: '',
          id: '',
        },
      },
      illustratedMessageTitle: await this.i18nService.getTranslationAsync(
        'ERROR_UNIDENTIFIED_TITLE',
      ),
      illustratedMessageText: '',
      buttons: [],
    };
  }

  private async getErrorEntityNotFoundConfig(
    id: string,
    sceneId: string,
    typeStr: string,
    typeStrPlural: string,
    gotoNavContext: any,
    buttons: any[],
  ) {
    return {
      sceneConfig: {
        scene: {
          url: `assets/moments/sapIllus-${sceneId}.svg`,
          id: `sapIllus-${sceneId}`,
        },
      },
      illustratedMessageTitle: await this.i18nService.getTranslationAsync(
        'ERROR_ENTITY_NOT_FOUND_TITLE',
        { entityType: typeStr, entityId: `<b>${id}</b>` },
      ),
      illustratedMessageText:
        typeStrPlural && gotoNavContext
          ? await this.i18nService.getTranslationAsync(
              'ERROR_ENTITY_NOT_FOUND_TEXT_LIST',
              {
                entityTypePlural: typeStrPlural,
                entityTypePlural_lowerCase: typeStrPlural.toLowerCase(),
              },
            )
          : await this.i18nService.getTranslationAsync(
              'ERROR_ENTITY_NOT_FOUND_TEXT_NO_LIST',
            ),
      buttons,
    };
  }
}
